import { API_HOST } from './index.js';
import { setAuthView, setAuthCardContext, showCodeGeneratingUI } from './utils/auth-ui.js';
import { showToast } from './utils/toast.js';

window.generatingCode = false;
let activeCode = null;
let copyHandlerAttached = false;

export async function checkForCodeGeneration() {
    console.log('Checking for code generation...');
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    if (code) {
        window.generatingCode = true;
        showCodeGeneratingUI();
        await generateCode();
    }
}

function renderCodeMessage(code, timeLeft) {
    document.getElementById('message').innerHTML = `
        <p class="code-label">Your login code</p>
        <p class="code-display" aria-label="Login code ${code.split('').join(' ')}">${code}</p>
        <p class="text-muted">Valid for ${timeLeft} second${timeLeft === 1 ? '' : 's'}. Enter it in the app to continue.</p>`;
}

function setupCopyButton(code) {
    activeCode = code;
    const copyBtn = document.getElementById('copy-code-btn');
    copyBtn.classList.remove('copied');
    copyBtn.textContent = 'Copy code';
    copyBtn.disabled = false;
    document.getElementById('copy-code').classList.remove('auth-panel-hidden');

    if (copyHandlerAttached) {
        return;
    }

    copyHandlerAttached = true;
    copyBtn.addEventListener('click', async () => {
        if (!activeCode) {
            return;
        }
        await navigator.clipboard.writeText(activeCode);
        copyBtn.classList.add('copied');
        copyBtn.textContent = 'Copied!';
        showToast('Code copied to clipboard');
        setTimeout(() => {
            if (activeCode) {
                copyBtn.classList.remove('copied');
                copyBtn.textContent = 'Copy code';
            }
        }, 2000);
    });
}

function hideCopyButton() {
    activeCode = null;
    document.getElementById('copy-code').classList.add('auth-panel-hidden');
    document.getElementById('copy-code-btn').disabled = true;
}

async function generateCode() {
    console.log('Generating code...');

    try {
        const response = await fetch(`${API_HOST}/auth/generate-code`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const data = await response.json();
        const code = data.code;
        const expiryTime = data.expiryTime;

        setAuthCardContext('authorise');
        setAuthView('message');
        renderCodeMessage(code, Math.floor((expiryTime - Date.now()) / 1000));
        setupCopyButton(code);

        const countDown = setInterval(async () => {
            const timeLeft = Math.floor((expiryTime - Date.now()) / 1000);

            if (timeLeft <= 0) {
                document.getElementById('message').innerHTML = '<p class="code-label">Your login code</p><p class="code-display">Expired</p>';
                hideCopyButton();
                clearInterval(countDown);
                return;
            }

            try {
                const [valid, error] = await checkCodeValidity(code);

                if (!valid) {
                    if (error === 'Code already used') {
                        document.getElementById('message').innerHTML = '<p class="status-success"><strong>Login complete</strong> &#10003;</p>';
                    } else if (error === 'Code expired') {
                        document.getElementById('message').innerHTML = '<p class="status-error">Login code <strong>expired</strong> &#10007;</p>';
                    } else if (error === 'New code generated') {
                        document.getElementById('message').innerHTML = '<p class="status-error"><strong>Error:</strong> Another code has been generated. &#10007;</p>';
                    } else {
                        document.getElementById('message').innerHTML = `<p class="status-error">Login code <strong>error</strong></p><p class="text-muted">${error}</p>`;
                    }
                    hideCopyButton();
                    clearInterval(countDown);
                    return;
                }
            } catch (error) {
                document.getElementById('message').innerHTML = `<p class="status-error">Login code <strong>error</strong></p><p class="text-muted">${error.message}</p>`;
                hideCopyButton();
                clearInterval(countDown);
                return;
            }

            renderCodeMessage(code, timeLeft);
        }, 1000);
    } catch (error) {
        console.error(error);
        setAuthCardContext('authorise');
        setAuthView('message');
        document.getElementById('message').innerHTML = '<p class="status-error">Could not generate a login code.</p><p class="text-muted">Please try again later.</p>';
        hideCopyButton();
    }
}

async function checkCodeValidity(code) {
    const response = await fetch(`${API_HOST}/auth/check-code-valid`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code })
    });

    const data = await response.json();

    if (!response.ok) {
        return [data.valid, data.error];
    }

    return [data.valid, data.error];
}
